import { useState, useMemo, useCallback } from "react";
import { Link } from "wouter";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { Building2, MapPin, Globe, CheckCircle2, Plus, AlertTriangle, Bell } from "lucide-react";
import { AppLayout } from "../components/layout/AppLayout";
import { FilterSheet } from "../components/layout/FilterSheet";
import { FilterSidebar } from "../components/filters";
import { Button } from "../components/ui/button";
import { DanceStyleBadge } from "../components/calendar/DanceStyleBadge";
import { VenueCreateModal } from "../components/forms/VenueCreateModal";
import { VenueSuggestModal } from "../components/forms/VenueSuggestModal";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useDebounce } from "../hooks/useDebounce";
import { useFavorites } from "../hooks/useFavorites";
import { getDistanceMiles } from "../lib/distance";
import type { GeocodingResult } from "../lib/geocoding";

export function VenuesPage() {
  const { isLoggedIn, user } = useCurrentUser();
  const isStudioOwner = user?.accountType === "studio";
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);

  // Filter state
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [distance, setDistance] = useState(25);
  const [userLocation, setUserLocation] = useState<GeocodingResult | null>(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const { favorites } = useFavorites();
  const debouncedSearch = useDebounce(searchQuery, 300);

  const handleLocationChange = useCallback((location: GeocodingResult | null) => {
    setUserLocation(location);
  }, []);

  const venues = useQuery(api.venues.getAllVenues, {
    query: debouncedSearch || undefined,
  });
  const activeNoticeVenueIds = useQuery(api.venueNotices.getActiveNoticeVenueIds);
  const activeNoticeSet = new Set(activeNoticeVenueIds ?? []);

  const filteredVenues = useMemo(() => {
    if (!venues) return [];
    let result = [...venues];

    // Filter by dance styles
    if (selectedStyles.length > 0) {
      result = result.filter((venue) =>
        venue.danceStyles?.some((style: string) => selectedStyles.includes(style))
      );
    }

    // Filter by favorites. Keyed on venue id rather than name, so renaming a
    // venue no longer silently breaks everyone's favorite.
    if (showFavoritesOnly) {
      result = result.filter((venue) => favorites.venueIds.includes(venue._id));
    }

    // Filter by distance from user location
    if (userLocation) {
      result = result.filter((venue) => {
        if (!venue.latitude || !venue.longitude) return false;
        const venueDistance = getDistanceMiles(
          userLocation.latitude,
          userLocation.longitude,
          venue.latitude,
          venue.longitude
        );
        return venueDistance <= distance;
      });
    }

    return result;
  }, [venues, selectedStyles, showFavoritesOnly, favorites, userLocation, distance]);

  const filterProps = {
    searchQuery,
    onSearchChange: setSearchQuery,
    startDate,
    endDate,
    onStartDateChange: setStartDate,
    onEndDateChange: setEndDate,
    zipCode,
    onZipCodeChange: setZipCode,
    distance,
    onDistanceChange: setDistance,
    location: userLocation,
    onLocationChange: handleLocationChange,
    selectedStyles,
    onStyleChange: setSelectedStyles,
    showFavoritesOnly,
    onFavoritesChange: setShowFavoritesOnly,
  };

  return (
    <AppLayout
      sidebar={<FilterSidebar {...filterProps} />}
    >
      <FilterSheet
        {...filterProps}
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
      />
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl text-foreground mb-1 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary" />
              Venues
            </h1>
            <p className="text-muted-foreground text-sm">
              Places where dances happen in your community.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setShowSuggestModal(true)}
            >
              <Plus className="w-4 h-4" />
              Suggest a Venue
            </Button>
            {isStudioOwner && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4" />
                Register My Studio
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Venue count */}
      <div className="text-sm text-muted-foreground mb-6">
        {filteredVenues.length} venue{filteredVenues.length !== 1 ? "s" : ""}
      </div>

      {/* Venue list */}
      {venues === undefined ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-secondary/30 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredVenues.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border/50">
          <Building2 className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground mb-1">
            {searchQuery ? "No venues match your filters." : "No venues registered yet."}
          </p>
          {!searchQuery && (
            <p className="text-sm text-muted-foreground">
              Be the first to register a dance venue!
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredVenues.map((venue) => {
            const hasAlert = activeNoticeSet.has(venue._id);
            return (
            <Link
              key={venue._id}
              href={`/venue/${venue._id}`}
              className="block bg-card rounded-xl border border-border/50 p-4 hover:border-primary/40 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 p-2 rounded-lg flex-shrink-0 ${hasAlert ? "bg-amber-100 dark:bg-amber-900/30" : "bg-primary/10"}`}>
                  <Building2 className={`w-4 h-4 ${hasAlert ? "text-amber-600 dark:text-amber-400" : "text-primary"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-foreground">{venue.name}</h3>
                    {venue.claimStatus === "verified" && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                    {venue.claimStatus === "pending" && (
                      <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                        Pending
                      </span>
                    )}
                    {hasAlert && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                        <Bell className="w-3 h-3" />
                        Notice
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{venue.address}</span>
                  </div>
                  {venue.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                      {venue.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {venue.danceStyles && venue.danceStyles.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {venue.danceStyles.map((style: string) => (
                          <DanceStyleBadge key={style} slug={style} size="sm" />
                        ))}
                      </div>
                    )}
                    {venue.website && (
                      <span className="flex items-center gap-1 text-xs text-primary">
                        <Globe className="w-3 h-3" />
                        Website
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <VenueCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => setShowCreateModal(false)}
        />
      )}
      {showSuggestModal && (
        <VenueSuggestModal
          onClose={() => setShowSuggestModal(false)}
          onCreated={() => setShowSuggestModal(false)}
        />
      )}
    </AppLayout>
  );
}
